import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import GoogleSignInButton from "../components/GoogleSignInButton";
import LettuceTalkLogo from "../images/logo.png";

function AppLandingView() {
  const navigate = useNavigate();
  const auth = getAuth();
  const [authError, setAuthError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Auto-redirect if user is already signed in
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setLoading(false);
      if (currentUser) {
        navigate("/home", { replace: true });
      }
    });

    return () => unsubscribe();
  }, [navigate, auth]);

  const handleSignInSuccess = (user) => {
    console.log("Successfully signed in:", user.displayName);
    setAuthError("");
  };

  const handleSignInError = (error) => {
    console.error("Sign in error:", error);
    setAuthError("Failed to sign in");
  };

  const handleBackToWeb = () => {
    navigate("/", { replace: true });
  };

  function FloatingHearts() {
    // Generate random properties for each heart
    const hearts = Array.from({ length: 12 }).map(() => ({
      left: Math.random() * 100, // percent
      fontSize: 24 + Math.random() * 32, // px
      animationDelay: Math.random() * 6, // seconds
    }));

    return (
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 overflow-hidden"
      >
        {hearts.map((props, i) => (
          <svg
            key={i}
            width={32}
            height={32}
            viewBox="0 0 32 29.6"
            fill="none"
            className="absolute animate-float-heart"
            style={{
              left: `${props.left}%`,
              top: "100%",
              animationDelay: `${props.animationDelay}s`,
              opacity: 0.5,
              filter: "blur(0.5px)",
            }}
          >
            <path
              d="M23.6,0c-3.4,0-6.4,2.7-7.6,5.3C14.8,2.7,11.8,0,8.4,0C3.8,0,0,3.8,0,8.4
              c0,9.3,16,20.7,16,20.7s16-11.4,16-20.7C32,3.8,28.2,0,23.6,0z"
              fill="#FFD1DC"
            />
          </svg>
        ))}
        <style>
          {`
          @keyframes float-heart {
            0% { transform: translateY(0) scale(1) rotate(0deg); opacity: 0.7; }
            50% { opacity: 1; }
            100% { transform: translateY(-120vh) scale(1.2) rotate(20deg); opacity: 0; }
          }
          .animate-float-heart {
            animation: float-heart 8s linear infinite; /* slowed down from 8s to 14s */
          }
        `}
        </style>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>LettuceTalk App Login</title>
      </Helmet>

      <main className="relative bg-gradient-to-b from-yellow-100 to-white min-h-screen flex flex-col items-center justify-center px-6 text-center font-sans">
        <button
          type="button"
          onClick={handleBackToWeb}
          className="absolute top-6 left-6 inline-flex items-center gap-2 rounded-full border border-[#4D7C0F] bg-white/90 px-4 py-2 text-sm font-semibold text-[#4D7C0F] shadow-sm transition hover:bg-[#F8F8F2]"
        >
          ← Back to LettuceTalk Website
        </button>

        <img
          src={LettuceTalkLogo}
          alt="LettuceTalk Logo"
          className="w-32 mb-6"
        />

        <h1 className="font-title text-4xl sm:text-4xl lg:text-6xl font-bold text-[#4D7C0F] mb-3 sm:mb-4">
          LettuceTalk App
        </h1>

        <p className="text-base sm:text-lg lg:text-xl mb-6 text-[#3F3F46] max-w-sm lg:max-w-none">
          Caregiving conversation cards for your whole family
        </p>

        <div className="flex flex-col items-center justify-center gap-3 mt-8 mb-4">
          <GoogleSignInButton
            onSuccess={handleSignInSuccess}
            onError={handleSignInError}
            className="btn-primary"
            loading={loading}
          >
            Sign in or Create Account
          </GoogleSignInButton>
        </div>

        {authError && (
          <div className="alert alert-error mt-4 text-sm max-w-sm mx-auto">
            <span>{authError}</span>
          </div>
        )}

        <FloatingHearts />

        <div className="fixed bottom-8 text-lg">
          An{" "}
          <a
            href="https://imoonshots.com"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium hover:underline transition"
          >
            Impact Moonshots
          </a>{" "}
          Production
        </div>
      </main>
    </>
  );
}

export default AppLandingView;
